import React, { useState, useRef, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, Facebook, Instagram } from 'lucide-react';
import emailjs from '@emailjs/browser';
import axios from 'axios';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingContactInfo, setIsLoadingContactInfo] = useState(true);
  const [error, setError] = useState('');
  const [contactInfo, setContactInfo] = useState({
    emailAddress: '',
    phoneNumber: '',
    address: '',
    googleMapLink: '',
    workingHours: '',
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: ''
    }
  });
  const form = useRef();
  
  const baseURL="https://backendweb.properteck.lk";

  useEffect(() => {
    const fetchContactInfo = async () => {
      setIsLoadingContactInfo(true);
      try {
        const response = await axios.get(`${baseURL}/api/contact/get`);
        const data = response.data.data;

        if (data) {
          setContactInfo({
            emailAddress: data.emailAddress || '',
            phoneNumber: data.phoneNumber || '',
            address: data.address || '',
            googleMapLink: data.googleMapLink || '',
            workingHours: data.workingHours || '',
            socialMedia: {
              facebook: data.socialMedia?.facebook || '',
              instagram: data.socialMedia?.instagram || '',
              twitter: data.socialMedia?.twitter || '',
              linkedin: data.socialMedia?.linkedin || ''
            }
          });
        } else {
          setError('No contact information available');
        }
      } catch (error) {
        console.error('Fetch contact info error:', error);
        setError(
          error.response?.data?.message || 'Failed to load contact information'
        );
      } finally {
        setIsLoadingContactInfo(false);
      }
    };

    fetchContactInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    emailjs
      .sendForm(
        'service_9jb56on', // EmailJS service ID
        'template_mkiyodt', // EmailJS template ID
        form.current,
        'NTs3XfjAmLzPslPax' // EmailJS public key
      )
      .then((result) => {
        console.log('Email sent successfully:', result.text);
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      })
      .catch((error) => {
        console.error('Email sending failed:', error.text);
        alert('There was an error sending your message. Please try again.');
      })
      .finally(() => {
        setLoading(false);
        setTimeout(() => {
          setSubmitted(false);
        }, 3000);
      });
  };

  return (
    <div className="contact-container">
      <div className="contact-wrapper">
        <h1 className="contact-title">Contact Us</h1>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {isLoadingContactInfo ? (
          <div className="loading-message">
            <p>Loading contact information...</p>
          </div>
        ) : (
          <div className="contact-info-grid">
            <div className="contact-card">
              <div className="icon-wrapper phone-icon">
                <Phone className="icon" />
              </div>
              <h3 className="card-title">Phone</h3>
              <p className="card-text">
                {contactInfo.phoneNumber }
              </p>
              <p className="card-text">
                {contactInfo.workingHours }
              </p>
            </div>

            <div className="contact-card">
              <div className="icon-wrapper email-icon">
                <Mail className="icon" />
              </div>
              <h3 className="card-title">Email</h3>
              <p className="card-text">
                {contactInfo.emailAddress }
              </p>
            </div>

            <div className="contact-card">
              <div className="icon-wrapper location-icon">
                <MapPin className="icon" />
              </div>
              <h3 className="card-title">Office</h3>
              <p className="card-text">
                {contactInfo.address }
              </p>
              <p className="card-text">
                <a
                  href={contactInfo.googleMapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on Google Maps
                </a>
              </p>
            </div>

            <div className="contact-card">
              <div className="icon-wrapper social-icon">
                <Facebook className="icon" />
              </div>
              <h3 className="card-title">Social Media</h3>
              <p className="card-text">
                <a
                  href={contactInfo.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Facebook
                </a>
              </p>
              <p className="card-text">
                <a
                  href={contactInfo.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Instagram
                </a>
              </p>
              <p className="card-text">
                <a
                  href={contactInfo.socialMedia.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Twitter
                </a>
              </p>
            </div>
          </div>
        )}

        <div className="form-container">
          <h2 className="form-title">Send us a message</h2>

          {submitted && (
            <div className="success-message">
              Thank you for your message! We'll get back to you soon.
            </div>
          )}

          <form ref={form} onSubmit={handleSubmit}>
            <div className="input-row">
              <div className="input-group">
                <label htmlFor="name" className="input-label">Your Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="email" className="input-label">Your Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field"
                  required
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="subject" className="input-label">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="input-field"
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="message" className="input-label">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="5"
                className="textarea-field"
                required
              ></textarea>
            </div>

            <input
              type="hidden"
              name="to_email"
              value={contactInfo.emailAddress || 'udayangad56@gmail.com'}
            />

            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              <Send className="send-icon" />
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;